// TrainPods console navigation: used by the sidebar and the in-page tabs.
import React from 'react';
import {
  GaugeIc, TrendIc, ServersIc, PromptIc, KeyIc, HomeIc, GridIc, ChipIc, TerminalIc, DriveIc, LayersIc, BoltIc,
  ChatIc, DbIc, FolderIc, CardIc, SlidersIc,
} from '../components/console/icons';

export interface ConsoleNavItem {
  id: string;
  name: string;
  href: string;
  icon: (p: React.SVGProps<SVGSVGElement>) => React.ReactElement;
  /** Another Podstack app: a full-page link instead of an in-app route. */
  external?: boolean;
}

export const CONSOLE_GROUP = { label: 'TrainPods', hint: 'Training studio' };

export const CONSOLE_NAV: ConsoleNavItem[] = [
  { id: 'nav-dashboard', name: 'Dashboard', href: '/console/dashboard', icon: GaugeIc },
  { id: 'nav-gpus', name: 'GPU catalog', href: '/console/gpus', icon: TrendIc },
  { id: 'nav-instances', name: 'Instances', href: '/console/instances', icon: ServersIc },
  { id: 'nav-connect', name: 'Connect', href: '/console/connect', icon: PromptIc },
  { id: 'nav-keys', name: 'Keys & SSH', href: '/console/keys', icon: KeyIc },
];

// Sidebar: the same groups and items as customer-portal's config/consoleNav.ts,
// so every Podstack console has one navigation. TrainPods' own pages are
// in-app routes; everything else lives in the customer portal or the inference
// portal and navigates there in this tab.

export interface ConsoleNavGroup {
  id: string;
  label?: string;
  hint?: string;
  items: ConsoleNavItem[];
}

const trim = (u: string) => u.replace(/\/+$/, '');
const PORTAL_URL = trim(process.env.REACT_APP_CUSTOMER_PORTAL_URL || 'https://cloud.podstack.ai/portal');
const INFERENCE_PORTAL_URL = trim(process.env.REACT_APP_INFERENCE_PORTAL_URL || 'https://inference.podstack.ai/inference');

// Sandboxes and Managed MLOps are flag-gated in customer-portal; both are on in
// production, so they're listed here unconditionally.
const portal = (id: string, name: string, path: string, icon: ConsoleNavItem['icon']): ConsoleNavItem => ({
  id, name, href: `${PORTAL_URL}${path}`, icon, external: true,
});

export const SIDEBAR_NAV: ConsoleNavGroup[] = [
  {
    id: 'start',
    items: [portal('nav-home', 'Home', '/', HomeIc), portal('nav-services', 'All services', '/services', GridIc)],
  },
  {
    id: 'quickpods',
    label: 'QuickPods',
    hint: 'GPU cloud',
    items: [
      portal('nav-pods', 'GPU Pods', '/pods', ChipIc),
      portal('nav-sandboxes', 'Sandboxes', '/sandboxes', TerminalIc),
      portal('nav-volumes', 'NFS volumes', '/volumes', DriveIc),
      portal('nav-managed-mlops', 'Managed MLOps', '/managed-mlops', LayersIc),
    ],
  },
  { id: 'trainpods', label: CONSOLE_GROUP.label, hint: CONSOLE_GROUP.hint, items: CONSOLE_NAV },
  {
    id: 'inference',
    label: 'Inference',
    hint: 'Serverless models',
    items: [
      { id: 'nav-inf-models', name: 'Models', href: `${INFERENCE_PORTAL_URL}/console/models`, icon: BoltIc, external: true },
      { id: 'nav-inf-playground', name: 'Playground', href: `${INFERENCE_PORTAL_URL}/console/playground`, icon: ChatIc, external: true },
      { id: 'nav-inf-keys', name: 'API keys', href: `${INFERENCE_PORTAL_URL}/console/keys`, icon: KeyIc, external: true },
    ],
  },
  {
    id: 'storage',
    label: 'Storage',
    items: [portal('nav-drive', 'Object storage', '/drive', DbIc)],
  },
  {
    id: 'account',
    label: 'Account',
    items: [
      portal('nav-projects', 'Projects', '/projects', FolderIc),
      portal('nav-billing', 'Billing', '/billing', CardIc),
      portal('nav-settings', 'Settings', '/settings', SlidersIc),
    ],
  },
];
